<section class="proof-viewer" aria-label="Paginated notice proof">
  <div class="proof-heading">
    <h3>Paginated final output</h3>
    <button
      uiButton="secondary"
      type="button"
      (click)="download()"
      [disabled]="disabled || !displayedProof"
      aria-label="Download PDF"
    >
      Download PDF
    </button>
  </div>
  @if (displayedProof; as result) {
    <p class="proof-caption">
      {{ result.isSynthetic ? 'Synthetic' : 'Captured' }} ·
      {{ result.exampleCode }} · {{ result.pageCount }}
      {{ result.pageCount === 1 ? 'page' : 'pages' }} · exact saved source
    </p>
    <details
      #proofDetails
      class="proof-details"
      [style.--proof-content-height]="(viewport?.height ?? 0) + 'px'"
      (keydown.escape)="
        proofDetails.open = false;
        proofSummary.focus();
        $event.preventDefault();
        $event.stopPropagation()
      "
    >
      <summary #proofSummary>Proof details</summary>
      <dl>
        @if (result.terminologySnapshot) {
          <dt>Published wording</dt>
          <dd>
            <button
              uiButton="secondary"
              type="button"
              [disabled]="disabled"
              (click)="checkWording.emit()"
            >
              Check wording
            </button>
          </dd>
        }
        <dt>Source SHA-256</dt>
        <dd>{{ result.sourceHash }}</dd>
        <dt>Context SHA-256</dt>
        <dd>{{ result.contextHash }}</dd>
        <dt>PDF SHA-256</dt>
        <dd>{{ result.pdfHash }}</dd>
        <dt>Renderer</dt>
        <dd>{{ result.rendererCode }} · {{ result.rendererVersion }}</dd>
        @if (result.terminologySnapshot; as terminology) {
          <dt>Wording capture</dt>
          <dd>
            {{ terminology.context.localeCode }} ·
            {{ terminology.context.channelCode }} ·
            {{ terminology.resolutionUtc }}
          </dd>
          <dt>Vocabulary SHA-256</dt>
          <dd>{{ terminology.semanticHash }}</dd>
          <dt>Capture SHA-256</dt>
          <dd>{{ terminology.captureHash }}</dd>
          @for (node of terminology.nodes; track node.key) {
            <dt>Registered {{ node.tokenCode }}</dt>
            <dd>
              {{ node.text }} · {{ node.definitionKindCode
              }}{{ node.familyCode ? ' · ' + node.familyCode : ''
              }}{{
                node.grammaticalFormCode ? ' · ' + node.grammaticalFormCode : ''
              }}
              @if (node.publicationId) {
                · publication {{ node.publicationId }} · revision
                {{ node.revisionId }}
              }
              @if (node.usedFallback) {
                · Registered fallback
              }
            </dd>
          }
        }
        @for (font of result.fontEvidence; track font.code) {
          <dt>Font {{ font.code }}</dt>
          <dd>{{ font.sha256 }}</dd>
        }
        @for (asset of result.assetEvidence; track asset.code) {
          <dt>Asset {{ asset.code }}</dt>
          <dd>{{ asset.sha256 }}</dd>
        }
        @if (result.contextEvidence; as context) {
          <dt>Registered context</dt>
          <dd>
            {{ context.reference.code }} v{{ context.reference.version }} ·
            {{ context.reference.hash }}
          </dd>
          <dt>Authorized scope</dt>
          <dd>
            {{ context.scopeTypeCode
            }}{{ context.scopeKey ? ' · ' + context.scopeKey : '' }}
          </dd>
          <dt>Source / time zone</dt>
          <dd>
            {{ context.sourceKind }} · {{ context.sourceKey }} ·
            {{ context.timeZone }}
          </dd>
          @for (term of context.termEvidence; track term.code) {
            <dt>Terminology {{ term.code }}</dt>
            <dd>
              {{ term.text }} ·
              {{ term.usedFallback ? 'Declared fallback' : 'Resolved wording' }}
              · {{ term.reasonCode
              }}{{
                term.configurationVersionCode
                  ? ' · ' + term.configurationVersionCode
                  : ''
              }}
            </dd>
          }
        }
      </dl>
    </details>
    <div
      class="proof-navigation"
      role="group"
      aria-label="Preview page controls"
      tabindex="0"
      (keydown)="pageKeydown($event)"
    >
      <button
        uiButton="secondary"
        type="button"
        (click)="setPage(pageNumber - 1)"
        [disabled]="disabled || pageNumber <= 1"
        aria-label="Previous preview page"
      >
        Previous
      </button>
      <label
        >Page<select
          uiControl
          name="preview-page"
          aria-label="Preview page"
          [ngModel]="pageNumber"
          (ngModelChange)="setPage($event)"
          [disabled]="disabled"
        >
          @for (number of pageNumbers; track number) {
            <option [ngValue]="number">{{ number }}</option>
          }</select
        ><span>of {{ pageCount }}</span></label
      >
      <button
        uiButton="secondary"
        type="button"
        (click)="setPage(pageNumber + 1)"
        [disabled]="disabled || pageNumber >= pageCount"
        aria-label="Next preview page"
      >
        Next
      </button>
      <label
        >Zoom<select
          uiControl
          name="preview-zoom"
          aria-label="Preview zoom"
          [ngModel]="zoom"
          (ngModelChange)="setZoom($event)"
          [disabled]="disabled"
        >
          <option [ngValue]="'fit-page'">Fit page</option>
          <option [ngValue]="'fit-width'">Fit width</option>
          <option [ngValue]="50">50%</option>
          <option [ngValue]="75">75%</option>
          <option [ngValue]="100">100%</option>
        </select></label
      >
      @if (actualZoom !== null) {
        <span class="actual-zoom" aria-label="Actual preview zoom"
          >{{ actualZoom }}%</span
        >
      }
      <span class="visually-hidden" aria-live="polite"
        >Preview page {{ pageNumber }} of {{ pageCount }}</span
      >
    </div>
    @if (selectedPage; as page) {
      <div
        #proofCanvas
        phmsNoticeProofViewport
        (viewportChange)="viewport = $event"
        class="preview-proof-canvas proof-canvas"
        tabindex="0"
        [attr.aria-label]="
          'Preview PDF page ' + page.number + ', scroll to inspect'
        "
      >
        <img
          [src]="'data:image/png;base64,' + page.pngBase64"
          [alt]="
            (result.isSynthetic ? 'Synthetic' : 'Captured') +
            ' notice final output, page ' +
            page.number
          "
          [style.visibility]="scale === null ? 'hidden' : null"
          [style.width.px]="page.widthPx * (scale ?? 0)"
          [style.height.px]="page.heightPx * (scale ?? 0)"
        />
      </div>
    }
  } @else {
    <ui-standard-state
      [kind]="disabled ? 'loading' : 'empty'"
      [message]="
        disabled
          ? 'Preparing notice proof'
          : 'No matching proof. Save and render a synthetic example.'
      "
    ></ui-standard-state>
  }
</section>
